/**
 * 重置密码页面
 */

import { zodResolver } from "@hookform/resolvers/zod";
import {
  GalleryVerticalEnd,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

import { isTokenExpired, useResetPasswordMutation } from "@/modules/auth";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/shared/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/shared/ui/input-group";
import { Spinner } from "@/shared/ui/spinner";




const ResetPasswordPage = () => {
  const { t } = useTranslation("reset-password");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const resetPasswordMutation = useResetPasswordMutation();

  const formSchema = z
    .object({
      password: z.string().min(6, {
        message: t("resetPassword.validation.password"),
      }),
      confirmPassword: z.string().min(1, {
        message: t("resetPassword.validation.confirmPassword"),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("resetPassword.validation.confirmPasswordNotMatch"),
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // 验证 token 有效性
  useEffect(() => {
    const validateToken = () => {
      if (!token) {
        setTokenValid(false);
        setTokenLoading(false);
        return;
      }

      setTokenLoading(true);
      const expired = isTokenExpired(token);

      if (expired === null) {
        console.error("Token validation failed: Unable to parse JWT");
        setTokenValid(false);
      } else if (expired === true) {
        console.log("Token has expired");
        setTokenValid(false);
      } else {
        console.log("Token is valid and not expired");
        setTokenValid(true);
      }

      setTokenLoading(false);
    };

    validateToken();
  }, [token]);

  // 重置成功后自动跳转倒计时
  useEffect(() => {
    if (isReset) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(ROUTE_PATHS.LOGIN);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isReset, navigate]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    resetPasswordMutation.mutate(
      {
        token: token || "",
        new_password: values.password,
      },
      {
        onSuccess: () => {
          form.reset();
          toast.success(t("resetPassword.successMessage"));
          setIsReset(true);
        },
      }
    );
  }

  // Token 验证中的加载状态
  if (tokenLoading) {
    return (
      <div className="flex w-full max-w-md flex-col gap-6">
          <a href={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
            <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
              <GalleryVerticalEnd className="size-4" />
            </div>
            Pinbox
          </a>
          <Card>
            <CardContent className="flex flex-col items-center justify-center px-8 py-6">
              <Spinner className="mb-4" />
              <p className="text-muted-foreground">
                {t("loading.verifyingToken")}
              </p>
            </CardContent>
          </Card>
        </div>
    );
  }

  // Token 无效的错误状态
  if (tokenValid === false) {
    return (
      <div className="flex w-full max-w-md flex-col gap-6">
          <a href={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
            <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
              <GalleryVerticalEnd className="size-4" />
            </div>
            Pinbox
          </a>
          <Card>
            <CardHeader className="px-8 text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-xl">{t("tokenError.title")}</CardTitle>
              <CardDescription>{t("tokenError.description")}</CardDescription>
            </CardHeader>
            <CardContent className="px-8">
              <div className="mb-6 space-y-2">
                <p className="text-sm font-medium">
                  {t("tokenError.reasonsTitle")}
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {(
                    t("tokenError.reasons", { returnObjects: true }) as string[]
                  ).map((reason: string, index: number) => (
                    <li key={index} className="flex items-center gap-2">
                      <span className="w-1 h-1 bg-muted-foreground rounded-full"></span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                className="h-11 w-full"
                onClick={() => navigate(ROUTE_PATHS.SEND_RESET_LINK)}
              >
                {t("tokenError.submitButton")}
              </Button>
            </CardContent>
          </Card>
        </div>
    );
  }

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
        <a href={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
          <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
            <GalleryVerticalEnd className="size-4" />
          </div>
          Pinbox
        </a>
        <div className={cn("flex flex-col gap-6")}>
          {!isReset ? (
            <Card>
              <CardHeader className="px-8 text-center">
                <CardTitle className="text-xl">
                  {t("resetPassword.title")}
                </CardTitle>
                <CardDescription>
                  {t("resetPassword.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-8">
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <FieldGroup>
                    <Controller
                      name="password"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="password">
                            {t("resetPassword.passwordLabel")}
                          </FieldLabel>
                          <InputGroup className="h-11">
                            <InputGroupInput
                              {...field}
                              className="h-full"
                              aria-invalid={fieldState.invalid}
                              id="password"
                              type={showPassword ? "text" : "password"}
                              placeholder={t(
                                "resetPassword.passwordPlaceholder"
                              )}
                              autoComplete="off"
                            />
                            <InputGroupAddon align="inline-end">
                              <InputGroupButton
                                aria-label="Eye"
                                title="toggle password visibility"
                                size="icon-xs"
                                onClick={togglePasswordVisibility}
                                type="button"
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </InputGroupButton>
                            </InputGroupAddon>
                          </InputGroup>
                          <FieldDescription>
                            {t("resetPassword.passwordDescription")}
                          </FieldDescription>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />

                    <Controller
                      name="confirmPassword"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="confirmPassword">
                            {t("resetPassword.confirmPasswordLabel")}
                          </FieldLabel>
                          <InputGroup className="h-11">
                            <InputGroupInput
                              {...field}
                              className="h-full"
                              aria-invalid={fieldState.invalid}
                              id="confirmPassword"
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder={t(
                                "resetPassword.confirmPasswordPlaceholder"
                              )}
                              autoComplete="off"
                            />
                            <InputGroupAddon align="inline-end">
                              <InputGroupButton
                                aria-label="Eye"
                                title="toggle password visibility"
                                size="icon-xs"
                                onClick={toggleConfirmPasswordVisibility}
                                type="button"
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </InputGroupButton>
                            </InputGroupAddon>
                          </InputGroup>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />
                    <Field>
                      <Button
                        type="submit"
                        className="h-11"
                        disabled={resetPasswordMutation.isPending}
                      >
                        {resetPasswordMutation.isPending && <Spinner />}
                        {t("resetPassword.submitButton")}
                      </Button>
                      <FieldDescription className="text-center">
                        {t("noAccount")}{" "}
                        <Link className="font-medium" to={ROUTE_PATHS.LOGIN}>
                          {" "}
                          {t("login")}
                        </Link>
                      </FieldDescription>
                    </Field>
                  </FieldGroup>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="px-8 pb-4 text-center">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="absolute inset-0 w-16 h-16 bg-green-200 rounded-full animate-ping opacity-20"></div>
                  </div>
                </div>
                <CardTitle className="text-xl">
                  {t("resetPasswordSuccess.title")}
                </CardTitle>
                <CardDescription>
                  {t("resetPasswordSuccess.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-8 pt-2">
                <div className="space-y-4">
                  <Button
                    onClick={() => navigate(ROUTE_PATHS.LOGIN)}
                    className="h-11 w-full"
                  >
                    {t("resetPasswordSuccess.submitButton")}
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    <Trans
                      i18nKey="autoRedirect.message"
                      values={{ countdown }}
                      components={{
                        strong: <span className="font-medium" />,
                      }}
                      ns="reset-password"
                    />
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
  );
};

export default ResetPasswordPage;
